<template>
  <div class="pa-2">
    <system-health-banner
      title="Progressive Transportation Pipeline"
      :subtitle="bannerSubtitle"
      :status="summary.status || 'unknown'"
    />

    <div class="mb-2">
      <status-chip
        :status="summary.status || 'unknown'"
        :label="'Queue: ' + queueSummaryLabel"
      />
      <status-chip
        :status="detectionChipStatus"
        :label="'Detections: ' + detectionSummaryLabel"
        tooltip="Pending beacon detection records waiting to be processed from progressive_ticket_detection. Old detections can indicate the progressive transportation worker is not draining its input backlog."
      />
      <status-chip
        :status="stuckChipStatus"
        :label="'Stuck: ' + (summary.stuckCount || 0)"
      />
    </div>

    <v-btn
      outline
      color="primary"
      :loading="apiInProgress"
      :disabled="apiInProgress"
      @click="detailDialog = true"
    >
      View per-bus details
    </v-btn>

    <detail-dialog
      v-model="detailDialog"
      title="Progressive Transportation Pipeline Details"
      :max-width="1200"
    >
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="apiInProgress"
        :pagination.sync="pagination"
        :total-items="totalItems"
        :rows-per-page-items="[10, 25, 50, 100]"
        @update:pagination="onPaginationChange"
      >
        <template slot="items" slot-scope="props">
          <tr>
            <td>{{ props.item.transportationLabel }}</td>
            <td>{{ props.item.status }}</td>
            <td>{{ props.item.pendingDetections || 0 }}</td>
            <td>{{ formatAge(props.item.oldestDetectionAgeSeconds) }}</td>
            <td>{{ formatAge(props.item.queueAgeSeconds) }}</td>
            <td class="text-xs-right">
              <v-btn
                small
                color="warning"
                outline
                :loading="isResetting(props.item.transportationProfileId)"
                :disabled="!props.item.isStuck || isResetting(props.item.transportationProfileId)"
                @click="resetRow(props.item)"
              >
                Reset to pending
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </detail-dialog>
  </div>
</template>

<script>
import {
  getApiProgressiveTicketQueue,
  resetApiProgressiveTicketQueue
} from '@/services/diagnostics-service'
import { addAlert } from '@/utilities/helpers'
import SystemHealthBanner from '../components/SystemHealthBanner.vue'
import StatusChip from '../components/StatusChip.vue'
import DetailDialog from '../components/DetailDialog.vue'
import { formatAgeShort } from '../components/status-mapping'

export default {
  name: 'realtime-diagnostics-boarding-tab',
  components: {
    SystemHealthBanner,
    StatusChip,
    DetailDialog
  },
  props: {
    refreshToken: {
      type: Number,
      default: 0
    },
    silentRefresh: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      apiInProgress: false,
      detailDialog: false,
      resettingId: null,
      items: [],
      totalItems: 0,
      summary: {
        status: 'unknown',
        queue: {},
        detectionBacklog: {},
        stuckCount: 0
      },
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 25,
        sortBy: 'dateUpdated'
      },
      headers: [
        { text: 'Bus', value: 'transportationLabel' },
        { text: 'Status', value: 'status' },
        { text: 'Pending Detections', value: 'pendingDetections', sortable: false },
        { text: 'Oldest Detection', value: 'oldestDetectionAgeSeconds', sortable: false },
        { text: 'Queue Age', value: 'queueAgeSeconds', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ]
    }
  },
  computed: {
    bannerSubtitle () {
      return 'Last activity: ' + formatAgeShort(this.summary.lastActivityAgeSeconds)
    },
    queueSummaryLabel () {
      const queue = this.summary.queue || {}
      return (queue.pending || 0) + ' pending • ' + (queue.processing || 0) + ' processing • ' + (queue.failed || 0) + ' failed'
    },
    detectionSummaryLabel () {
      const backlog = this.summary.detectionBacklog || {}
      return (backlog.total || 0) + ' total • oldest ' + formatAgeShort(backlog.oldestDetectionAgeSeconds)
    },
    detectionChipStatus () {
      const backlog = this.summary.detectionBacklog || {}
      const age = backlog.oldestDetectionAgeSeconds
      if (age === null || age === undefined) {
        return 'healthy'
      }
      if (age > 10800) {
        return 'critical'
      }
      if (age > 1800) {
        return 'warning'
      }
      return 'healthy'
    },
    stuckChipStatus () {
      const count = this.summary.stuckCount || 0
      return count > 0 ? 'critical' : 'healthy'
    }
  },
  watch: {
    refreshToken: {
      immediate: true,
      handler () {
        this.loadData(this.silentRefresh)
      }
    }
  },
  methods: {
    async loadData (silent) {
      if (!silent) {
        this.apiInProgress = true
      }

      try {
        const params = {
          limit: this.pagination.rowsPerPage || 25,
          offset: ((this.pagination.page || 1) - 1) * (this.pagination.rowsPerPage || 25)
        }
        const response = await getApiProgressiveTicketQueue(params)
        this.items = response && response.items ? response.items : []
        this.totalItems = response && response.pagination ? response.pagination.total || 0 : 0
        this.summary = response && response.summary ? response.summary : this.summary
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to load progressive transportation diagnostics'
        })
      } finally {
        if (!silent) {
          this.apiInProgress = false
        }
      }
    },
    onPaginationChange () {
      this.loadData(false)
    },
    formatAge (seconds) {
      return formatAgeShort(seconds)
    },
    isResetting (id) {
      return this.resettingId && String(this.resettingId) === String(id)
    },
    async resetRow (item) {
      if (!item || !item.transportationProfileId) {
        return
      }
      this.resettingId = item.transportationProfileId
      try {
        await resetApiProgressiveTicketQueue(item.transportationProfileId)
        addAlert({ type: 'success', message: 'Queue row reset to pending' })
        await this.loadData(false)
      } catch (err) {
        addAlert({
          type: 'error',
          message: err && err.message ? err.message : 'Failed to reset queue row'
        })
      } finally {
        this.resettingId = null
      }
    }
  }
}
</script>
